import { useEffect, useRef, useState } from "react";
import { Dashboard } from "./components/Dashboard";
import { AuthDialog } from "./components/AuthDialog";
import { ConfirmDialog } from "./components/Dialog";
import { Onboarding } from "./components/Onboarding";
import { Planner } from "./components/Planner";
import { ProfileDialog } from "./components/ProfileDialog";
import { TripDialog } from "./components/TripDialog";
import { SyncConflictDialog } from "./components/SyncConflictDialog";
import { deleteCloudState, getAccountSession, signOutAccount, type AccountSession } from "./auth";
import type { CreateTripInput, Trip } from "./types";
import { useRoam } from "./useRoam";
import { useAccountSync } from "./useAccountSync";
import { registerWebMCP, type WebMCPStatus } from "./webmcp";

export default function App() {
  const initialAuthError = new URL(window.location.href).searchParams.get("auth_error");
  const controller = useRoam();
  const controllerRef = useRef(controller);
  controllerRef.current = controller;
  const [webMCPStatus, setWebMCPStatus] = useState<WebMCPStatus>("checking");
  const [webMCPMessage, setWebMCPMessage] = useState("Looking for a WebMCP-capable browser…");
  const [tripDialogOpen, setTripDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(Boolean(initialAuthError));
  const [authError, setAuthError] = useState(initialAuthError ? "CodePawl could not complete sign-in. Please try again." : "");
  const [cloudDeleteOpen, setCloudDeleteOpen] = useState(false);
  const [account, setAccount] = useState<AccountSession | null>(null);
  const [accountPending, setAccountPending] = useState(true);
  const sync = useAccountSync(account, controller);

  useEffect(() => {
    const abort = new AbortController();
    void getAccountSession(abort.signal).then(setAccount).catch(() => setAccount(null)).finally(() => setAccountPending(false));
    const url = new URL(window.location.href);
    if (url.searchParams.has("auth") || url.searchParams.has("auth_error")) {
      url.searchParams.delete("auth");
      url.searchParams.delete("auth_error");
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    }
    return () => abort.abort();
  }, []);

  useEffect(() => {
    const registration = registerWebMCP({
      getController: () => controllerRef.current,
      setStatus: setWebMCPStatus,
      setMessage: setWebMCPMessage,
    });
    return () => registration.abort();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [controller.state.activeTripId, controller.state.onboardingComplete, controller.state.screen]);

  if (!controller.state.onboardingComplete) {
    return <><Onboarding onComplete={controller.completeOnboarding} onSignIn={() => setAuthOpen(true)} /><AuthDialog open={authOpen} initialError={authError} onClose={() => { setAuthOpen(false); setAuthError(""); }} /></>;
  }

  const activeTrip = controller.state.trips.find((trip) => trip.id === controller.state.activeTripId) ?? null;

  function openCreate() {
    setEditingTrip(null);
    setTripDialogOpen(true);
  }

  function openEdit(trip: Trip) {
    setEditingTrip(trip);
    setTripDialogOpen(true);
  }

  function saveTrip(input: CreateTripInput) {
    if (editingTrip) controller.updateTrip(editingTrip.id, input);
    else controller.createTrip(input);
  }

  const content = controller.state.screen === "planner" && activeTrip ? (
    <Planner
      controller={controller}
      trip={activeTrip}
      profile={controller.state.profile}
      onHome={() => controller.setScreen("trips")}
      onProfile={() => setProfileOpen(true)}
      onEditTrip={() => openEdit(activeTrip)}
      account={account}
      accountPending={accountPending}
      syncStatus={sync.status}
      onAccount={() => account ? setProfileOpen(true) : setAuthOpen(true)}
    />
  ) : (
    <Dashboard
      profile={controller.state.profile}
      trips={controller.state.trips}
      activity={controller.state.activity}
      onCreate={openCreate}
      onOpen={controller.openTrip}
      onEdit={openEdit}
      onDelete={controller.deleteTrip}
      onProfile={() => setProfileOpen(true)}
      account={account}
      accountPending={accountPending}
      syncStatus={sync.status}
      onAccount={() => account ? setProfileOpen(true) : setAuthOpen(true)}
    />
  );

  return (
    <>
      {content}
      <TripDialog open={tripDialogOpen} trip={editingTrip} defaultPace={controller.state.profile.pace} onClose={() => setTripDialogOpen(false)} onSave={saveTrip} />
      <AuthDialog open={authOpen} initialError={authError} onClose={() => { setAuthOpen(false); setAuthError(""); }} />
      <ProfileDialog open={profileOpen} profile={controller.state.profile} account={account} accountPending={accountPending} syncStatus={sync.status} syncMessage={sync.message} webMCPStatus={webMCPStatus} webMCPMessage={webMCPMessage} onClose={() => setProfileOpen(false)} onSave={controller.updateProfile} onSignIn={() => { setProfileOpen(false); setAuthOpen(true); }} onSignOut={() => { void signOutAccount().then(() => { setAccount(null); setProfileOpen(false); }); }} onDeleteCloud={() => { setProfileOpen(false); setCloudDeleteOpen(true); }} onReset={() => { setProfileOpen(false); setResetOpen(true); }} />
      <SyncConflictDialog conflict={sync.conflict} onUseCloud={sync.useCloud} onKeepDevice={() => void sync.keepDevice()} />
      <ConfirmDialog open={resetOpen} title="Reset all local data?" description="Your profile, trips, stops, and activity history will be erased from this browser. Export any trip you need first." confirmLabel="Reset Roam" destructive onClose={() => setResetOpen(false)} onConfirm={controller.resetAll} />
      <ConfirmDialog open={cloudDeleteOpen} title="Delete synced Roam data?" description="This permanently removes Roam trips stored in your CodePawl account. The current browser copy stays until you reset it." confirmLabel="Delete synced data" destructive onClose={() => setCloudDeleteOpen(false)} onConfirm={() => { void deleteCloudState().then(() => signOutAccount()).then(() => setAccount(null)); }} />
    </>
  );
}
